import {
  describeSiteWeightCoverage,
  exceedsFullCoverage,
  isPartlyCovered,
  isPartlyWeighted,
  PositionWeightCoverage,
  WeightedSite,
} from '@upright/common/model/input/companies/geolocation/site-weights';
import {
  allSiteRuleViolations,
  SITE_RULE_MESSAGES,
  SiteRuleViolation,
  siteRuleViolations,
} from '@upright/common/model/input/companies/geolocation/site-validation';
import { rowHasCoordinates, SiteRow } from './types';

export interface SiteValidationResult {
  isValid: boolean;
  /** Human-readable messages shown in the validation banner. */
  messages: string[];
  /** _uiId -> set of offending field names, used to flag the individual cells. Partial: only rows with errors have an entry. */
  rowErrors: Partial<Record<string, Set<keyof SiteRow>>>;
}

/**
 * Client-side validation run before a save: the per-site rules of `siteRuleViolations` (a site
 * type on every row, both coordinates or neither, no negative weight). Blocking here gives the
 * user an inline reason rather than a 400 from the save endpoint.
 *
 * Weights are checked only for sign here. The all-or-nothing-per-position rule
 * belongs to publish, not save — see `findPartlyWeightedPositions`.
 */
export function validateRows(rows: SiteRow[]): SiteValidationResult {
  const rowErrors: Partial<Record<string, Set<keyof SiteRow>>> = {};
  const violated = new Set<SiteRuleViolation>();

  for (const row of rows) {
    for (const { violation, fields } of siteRuleViolations(row)) {
      violated.add(violation);
      for (const field of fields) {
        (rowErrors[row._uiId ?? ''] ??= new Set()).add(field);
      }
    }
  }

  const messages = allSiteRuleViolations.filter(violation => violated.has(violation)).map(v => SITE_RULE_MESSAGES[v]);
  return { isValid: messages.length === 0, messages, rowErrors };
}

/** The projection weight resolution reads — see `site-weights.ts`. */
function toWeightedSite(row: SiteRow): WeightedSite {
  return { storedWeight: row.weight, supplyChainPosition: row.supplyChainPosition };
}

/**
 * Value-chain positions where some but not all sites carry a weight (PLT-6333).
 *
 * A weight is a share of its own position, so a position only means something
 * when every one of its sites is weighted or none is. A part-filled position
 * resolves to an equal split, silently discarding the weights that *were*
 * entered — so publishing one is refused.
 *
 * Deliberately NOT part of `validateRows`: that runs on save, and blocking a save
 * would trap a user who added a site and wants to keep its address before
 * deciding on weights. Weights only start counting at publish, which is where
 * this is checked (mirrored server-side in `site-locations/publish.ts`).
 *
 * Narrowed to coordinate-bearing rows because that is the set compute weighs —
 * a coordinate-less site takes no share, so its blank weight is not a gap.
 */
export function findPartlyWeightedPositions(rows: SiteRow[]): PositionWeightCoverage[] {
  const weighable = rows.filter(rowHasCoordinates);
  return describeSiteWeightCoverage(weighable.map(toWeightedSite)).filter(isPartlyWeighted);
}

/** The banner text naming the positions that still need weights. */
export function partlyWeightedMessage(partlyWeighted: PositionWeightCoverage[]): string {
  const positions = partlyWeighted.map(coverage => coverage.supplyChainPosition).join(' and ');
  return (
    `Give every ${positions} site a weight, or leave them all blank — a weight is a share of its own ` +
    'value-chain position, so a partly filled position would be split equally instead.'
  );
}

/**
 * Positions whose weights add up to more than 100% (PLT-6333).
 *
 * A weight is a percentage of its own position, so a position cannot exceed
 * full coverage — the total is either a slip or a set of relative shares that
 * needs rescaling. Refused at publish, like a part-filled position, so the
 * header's "(%)" stays true of anything newly entered.
 *
 * Judged over the coordinate-bearing sites, matching what compute weighs.
 */
export function findOverWeightedPositions(rows: SiteRow[]): PositionWeightCoverage[] {
  const weighable = rows.filter(rowHasCoordinates);
  return describeSiteWeightCoverage(weighable.map(toWeightedSite)).filter(exceedsFullCoverage);
}

/** One line per position that adds up to more than full coverage. */
export function overWeightedMessages(overWeighted: PositionWeightCoverage[]): string[] {
  return overWeighted.map(
    coverage =>
      `${coverage.supplyChainPosition} weights add up to ${Number(coverage.storedPercentSum.toPrecision(4))}%, ` +
      'which is more than the whole of that value chain position. Scale them down so they total 100% or less — ' +
      'less is fine, and means the sites listed cover only part of it.',
  );
}

/**
 * The total below which a position's weights are likelier a unit mistake than
 * genuine coverage. Fractions (0.5 / 0.3 / 0.2) and small relative shares
 * (3 / 3 / 3) land under it; real partial coverage — 70% of a footprint, 85% —
 * does not.
 */
const IMPLAUSIBLY_LOW_COVERAGE_PERCENT = 10;

/**
 * Positions whose weights are all filled in but total so little that they read as
 * almost no coverage (PLT-6333).
 *
 * A weight is a share of its own position, so a total of 0.97 says "these sites
 * cover 1% of this position" — which is what someone entering fractions, or
 * relative shares like 3/3/3, has said by accident. The "(%)" in the column
 * header makes that less likely but not impossible, and it is silent when it
 * happens: a real draft of 99 upstream sites totalling 0.97 is what prompted
 * this check.
 *
 * Warns rather than blocks — a company genuinely can cover 5% of its upstream
 * footprint, and nothing about the numbers is invalid.
 *
 * Two states are deliberately not flagged: an all-zero position, since zeroing
 * every site is the only way to say "leave this position out", and a
 * part-filled one, which `findPartlyWeightedPositions` already refuses.
 */
export function findImplausiblyLowPositions(rows: SiteRow[]): PositionWeightCoverage[] {
  const weighable = rows.filter(rowHasCoordinates);
  return describeSiteWeightCoverage(weighable.map(toWeightedSite)).filter(
    coverage => isPartlyCovered(coverage) && coverage.storedPercentSum < IMPLAUSIBLY_LOW_COVERAGE_PERCENT,
  );
}

/** One line per position whose weights probably were not meant as shares of 100. */
export function implausiblyLowMessages(lowPositions: PositionWeightCoverage[]): string[] {
  return lowPositions.map(coverage => {
    const total = Number(coverage.storedPercentSum.toPrecision(3));
    return (
      `${coverage.supplyChainPosition} weights total ${total}, so its ${coverage.siteCount} ` +
      `${coverage.siteCount === 1 ? 'site is' : 'sites are'} read as covering ${total}% of your ` +
      `${coverage.supplyChainPosition.toLowerCase()} footprint — the other ${Number((100 - coverage.storedPercentSum).toPrecision(3))}% is left out of the assessment. ` +
      'If you meant them as shares of each other, scale them up so the position totals 100.'
    );
  });
}

/** Flags the blank weight cells of each part-filled position, for the table's error styling. */
export function weightRowErrors(
  rows: SiteRow[],
  partlyWeighted: PositionWeightCoverage[],
): Partial<Record<string, Set<keyof SiteRow>>> {
  const positions = new Set(partlyWeighted.map(coverage => coverage.supplyChainPosition));
  const rowErrors: Partial<Record<string, Set<keyof SiteRow>>> = {};
  for (const row of rows) {
    if (positions.has(row.supplyChainPosition) && row.weight == null && rowHasCoordinates(row)) {
      (rowErrors[row._uiId ?? ''] ??= new Set()).add('weight');
    }
  }
  return rowErrors;
}
